/**
 * Traffic monitor — live view of OPBridge's inbound MIDI + OSC rings.
 *
 * Diagnostic surface (2026-08-11 — the "PWA traffic-monitor dashboard"
 * QUEUED item, unblocked once the migration sprint closed same day at
 * CP-086). OPBridge's traffic-monitor endpoints have been live since
 * Session 111 (v1.13.26, 2026-06-29); this is the first PWA-side consumer.
 *
 * Lazily connected — `connect()`/`disconnect()` are called by
 * `settings/store.ts`'s panel open/close, not at app boot. The two WS
 * sockets only open, and the ring buffers only accumulate, WHILE the
 * panel is actually on screen — nobody needs MIDI/OSC traffic buffered in
 * memory when nobody's looking at it. This also sidesteps needing a
 * performance-mode gate the way the always-on pollers do (projectSync's
 * 5s poll, etc.) — the panel wouldn't be open mid-set, and if it is, that
 * IS the operator's explicit ask to watch traffic.
 *
 * Shape: REST snapshot backfill (`GET /traffic/{midi,osc}/recent`) on
 * connect for instant content, then WS live push
 * (`GET /traffic/{midi,osc}/recent/ws`) for everything after — same
 * subprotocol bearer-auth pattern as `consumerWs.ts`
 * (`new WebSocket(url, [OPBRIDGE_SUBPROTOCOL, bearer])`, the only way a
 * browser can authenticate a WS with no custom-header support). `seq` is
 * per-stream monotonic (server-side, resets on daemon restart) — used to
 * skip any WS entries the REST backfill already delivered, so the
 * boundary between the two doesn't duplicate. A `*.lag` frame (broadcast
 * back-pressure on OPBridge's side) triggers a fresh REST re-backfill,
 * exactly the recovery OPBridge's own handler comment suggests.
 *
 * Client-side ring cap (200) is generous vs the server's 50 — the
 * dashboard is meant to hold a working session's worth of scrollback, not
 * just the daemon's small in-memory tail.
 */

import { createEffect, createRoot, createSignal, on, type Accessor } from 'solid-js';
import { bridges, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import { OPBRIDGE_SUBPROTOCOL } from './opbridgeConfig.ts';
import type { TrafficMidiEntry, TrafficOscEntry } from './httpClient.ts';

const CLIENT_RING_CAP = 200;
const RECONNECT_BASE_MS = 500;
const RECONNECT_CAP_MS = 8_000;

export type TrafficStreamStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface Wire { seq: number }

/** One direction's worth of module state — MIDI and OSC each get an
 *  independent instance via {@link makeChannel}, since their streams,
 *  sockets, and backoff timers are fully independent (a lagging OSC feed
 *  shouldn't touch MIDI's socket). */
function makeChannel<E extends Wire>(opts: {
  wsPath: string;
  restFetch: (client: ReturnType<NonNullable<ReturnType<typeof getConnection>>['httpClient']>) => Promise<E[]>;
  lagKind: string;
  endKind: string;
}) {
  const [log, setLog] = createSignal<E[]>([]);
  const [status, setStatus] = createSignal<TrafficStreamStatus>('idle');
  let socket: WebSocket | null = null;
  let lastSeq = 0;
  let reconnectAttempt = 0;
  let reconnectTimer: number | null = null;
  let boundCfg: BridgeConfig | null = null;

  function appendCapped(entry: E): void {
    setLog((prev) => {
      const next = prev.length >= CLIENT_RING_CAP ? prev.slice(prev.length - CLIENT_RING_CAP + 1) : prev.slice();
      next.push(entry);
      return next;
    });
  }

  async function backfill(cfg: BridgeConfig): Promise<void> {
    const conn = getConnection(cfg.id);
    if (!conn) return;
    try {
      const snapshot = await opts.restFetch(conn.httpClient());
      if (snapshot.length === 0) return;
      setLog(snapshot.slice(-CLIENT_RING_CAP));
      lastSeq = snapshot[snapshot.length - 1]!.seq;
    } catch { /* diagnostic surface — degrade silently, the WS may still work */ }
  }

  function scheduleReconnect(cfg: BridgeConfig): void {
    if (reconnectTimer !== null) return;
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(1.6, reconnectAttempt), RECONNECT_CAP_MS);
    reconnectAttempt += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (boundCfg === cfg) openSocket(cfg);
    }, delay);
  }

  function openSocket(cfg: BridgeConfig): void {
    if (!cfg.bearer) { setStatus('error'); return; }
    setStatus('connecting');
    const wsUrl = `${cfg.baseUrl.replace(/^http/i, 'ws')}${opts.wsPath}`;
    let sock: WebSocket;
    try {
      sock = new WebSocket(wsUrl, [OPBRIDGE_SUBPROTOCOL, cfg.bearer]);
    } catch {
      scheduleReconnect(cfg);
      return;
    }
    socket = sock;
    sock.addEventListener('open', () => {
      if (socket !== sock) return;
      reconnectAttempt = 0;
      setStatus('connected');
    });
    sock.addEventListener('message', (ev) => {
      if (socket !== sock) return;
      let msg: { kind?: string } & Partial<E>;
      try { msg = JSON.parse(ev.data as string); } catch { return; }
      if (msg.kind === opts.lagKind) { void backfill(cfg); return; }
      if (msg.kind === opts.endKind) return;
      if (typeof msg.seq !== 'number') return;
      if (msg.seq <= lastSeq) return; // already delivered by the REST backfill
      lastSeq = msg.seq;
      appendCapped(msg as E);
    });
    sock.addEventListener('close', () => {
      if (socket !== sock) return;
      socket = null;
      if (boundCfg === cfg) { setStatus('error'); scheduleReconnect(cfg); }
    });
    sock.addEventListener('error', () => { /* close fires too; that drives reconnect */ });
  }

  function bind(cfg: BridgeConfig): void {
    boundCfg = cfg;
    reconnectAttempt = 0;
    lastSeq = 0;
    void backfill(cfg);
    openSocket(cfg);
  }

  function unbind(): void {
    boundCfg = null;
    if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (socket) { try { socket.close(); } catch { /* */ } socket = null; }
    setStatus('idle');
  }

  function clear(): void { setLog([]); }

  return { log, status, bind, unbind, clear };
}

const midiChannel = makeChannel<TrafficMidiEntry>({
  wsPath: '/traffic/midi/recent/ws?direction=in',
  restFetch: (client) => client.trafficMidiRecent(),
  lagKind: 'traffic.midi.lag',
  endKind: 'traffic.midi.stream.end',
});
const oscChannel = makeChannel<TrafficOscEntry>({
  wsPath: '/traffic/osc/recent/ws?direction=in',
  restFetch: (client) => client.trafficOscRecent(),
  lagKind: 'traffic.osc.lag',
  endKind: 'traffic.osc.stream.end',
});

export const trafficMidiLog: Accessor<TrafficMidiEntry[]> = midiChannel.log;
export const trafficOscLog: Accessor<TrafficOscEntry[]> = oscChannel.log;
export const trafficMidiStatus: Accessor<TrafficStreamStatus> = midiChannel.status;
export const trafficOscStatus: Accessor<TrafficStreamStatus> = oscChannel.status;

function pickPrimaryConnected(): BridgeConfig | null {
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  return conn && conn.status() === 'connected' ? primary : null;
}

let active = false;
let disposeRoot: (() => void) | null = null;

/** Start the monitor: backfills + opens both WS sockets against whichever
 *  bridge is primary right now, and keeps rebinding if the primary
 *  changes while the panel stays open. Idempotent — a second call while
 *  already active is a no-op.
 *
 *  Wrapped in its own `createRoot` (same pattern `consumerWs.ts` uses) —
 *  this is called imperatively from the Settings panel's open handler,
 *  not during a component's render, so there's no ambient reactive owner
 *  for `createEffect` to attach to. `createRoot` gives one, and its
 *  returned `dispose` is what `disconnect()` calls to tear the effect
 *  down cleanly (rather than leaking a live subscription every time the
 *  panel is reopened). */
export function connect(): void {
  if (active) return;
  active = true;
  midiChannel.clear();
  oscChannel.clear();
  createRoot((dispose) => {
    disposeRoot = dispose;
    createEffect(on(() => pickPrimaryConnected()?.id ?? null, (id) => {
      midiChannel.unbind();
      oscChannel.unbind();
      if (!id) return;
      const cfg = pickPrimaryConnected();
      if (!cfg) return;
      midiChannel.bind(cfg);
      oscChannel.bind(cfg);
    }));
  });
}

/** Stop the monitor: disposes the reactive effect, closes both sockets,
 *  cancels any pending reconnect, and clears the ring buffers — nothing
 *  to show once nobody's watching, and reopening later should start from
 *  a clean backfill, not stale rows. */
export function disconnect(): void {
  if (!active) return;
  active = false;
  disposeRoot?.();
  disposeRoot = null;
  midiChannel.unbind();
  oscChannel.unbind();
  midiChannel.clear();
  oscChannel.clear();
}
